import React, { useState, useEffect, useRef } from 'react';
import './TypingAnimation.css';

const FADE_MS = 280;

/**
 * Cycles through phrases with a crossfade (no typewriter effect).
 *
 * @param {Object} props
 * @param {string} props.staticPrefix - Static text before the animated part
 * @param {string[]} props.rotatingPhrases - Array of phrases to cycle through
 * @param {number} [props.pauseDuration=3000] - Milliseconds each phrase stays visible
 * @param {string} [props.className] - Additional CSS classes
 */
const TypingAnimation = ({
  staticPrefix = '',
  rotatingPhrases = [],
  pauseDuration = 3000,
  className = '',
}) => {
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const fadeTimeoutRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!rotatingPhrases || rotatingPhrases.length <= 1) return undefined;

    intervalRef.current = setInterval(() => {
      setIsFading(true);
      fadeTimeoutRef.current = setTimeout(() => {
        setCurrentPhraseIndex((prev) => (prev + 1) % rotatingPhrases.length);
        setIsFading(false);
      }, FADE_MS);
    }, pauseDuration);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    };
  }, [rotatingPhrases, pauseDuration]);

  if (!rotatingPhrases || rotatingPhrases.length === 0) {
    return <span className={className}>{staticPrefix}</span>;
  }

  const currentPhrase = rotatingPhrases[currentPhraseIndex];
  const longestPhrase = rotatingPhrases.reduce(
    (best, p) => (String(p).length > best.length ? String(p) : best),
    ''
  );

  return (
    <span className={`typing-animation typing-animation--stable ${className}`.trim()}>
      <span className="typing-animation__measure" aria-hidden="true">
        {staticPrefix ? <span className="typing-animation__static">{staticPrefix} </span> : null}
        <span className="typing-animation__dynamic">{longestPhrase}</span>
      </span>
      <span className="typing-animation__live" aria-live="polite">
        {staticPrefix ? <span className="typing-animation__static">{staticPrefix} </span> : null}
        <span
          className={`typing-animation__dynamic${isFading ? ' typing-animation__dynamic--fading' : ''}`}
        >
          {currentPhrase}
        </span>
      </span>
    </span>
  );
};

export default TypingAnimation;
